package ai.wagemix.analytics

import ai.wagemix.logic.WMBaseDataGateway
import ai.wagemix.logic.analytics.WMAnalyticsProfile
import ai.wagemix.logic.analytics.analyticsProfileFlow
import com.fsryan.analytics.net.track.client.openpanel.FSOpenPanelTrackClientAPI
import com.fsryan.analytics.net.track.models.FSAnalyticsProfileUpdate
import com.fsryan.analytics.net.track.models.FSAnalyticsValue
import com.fsryan.analytics.net.track.models.kxs
import com.fsryan.foundation.net.client.isSuccess
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach

/**
 * Sends the signed-in user's OpenPanel profile (planning/analytics-plan.md §3, profile properties) on sign-in,
 * registration and every change of a profile field, such as a plan change. What the profile says is
 * [WMAnalyticsProfile]; a profile already sent from this browser is not sent again, across reloads too.
 */
internal interface WMAnalyticsProfileSync {

    /** Stops observing. */
    fun dispose()
}

internal fun WMAnalyticsProfileSync(
    dataGateway: WMBaseDataGateway,
    clientApi: FSOpenPanelTrackClientAPI,
    store: WMAnalyticsBrowserStore,
): WMAnalyticsProfileSync {
    return WMAnalyticsProfileSyncImpl(dataGateway = dataGateway, clientApi = clientApi, store = store)
}

private class WMAnalyticsProfileSyncImpl(
    dataGateway: WMBaseDataGateway,
    private val clientApi: FSOpenPanelTrackClientAPI,
    private val store: WMAnalyticsBrowserStore,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob()),
): WMAnalyticsProfileSync {

    init {
        dataGateway.analyticsProfileFlow().filterNotNull().onEach { profile -> send(profile) }.launchIn(scope)
    }

    private suspend fun send(profile: WMAnalyticsProfile) {
        val update = profileUpdateOf(profile)
        val fingerprint = update.toString()
        if (fingerprint == store.sentProfileFingerprint()) {
            return
        }
        val sent = try {
            clientApi.identify { profileUpdate(update) }.isSuccess
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            false
        }
        if (sent) {
            store.storeSentProfileFingerprint(fingerprint)
        }
    }

    override fun dispose() = scope.cancel()
}

private fun profileUpdateOf(profile: WMAnalyticsProfile): FSAnalyticsProfileUpdate {
    val properties = buildMap {
        put("plan", FSAnalyticsValue.Text(profile.plan.stringValue))
        profile.signedUpAt?.let { put("signed_up_at", FSAnalyticsValue.Text(it.toString())) }
        put("email_verified", FSAnalyticsValue.Flag(profile.emailVerified))
    }
    return FSAnalyticsProfileUpdate.kxs(
        avatarUrl = null,
        client = null,
        email = profile.email,
        firstName = profile.firstName,
        lastName = profile.lastName,
        profileId = profile.profileId,
        properties = properties,
    )
}
